import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Button,
  Card,
  DataTable,
  PageHead,
  Pager,
  SearchInput,
  StateBlock,
  type Column,
} from '@w7/ui'
import { count, EMPTY, lll } from '@/lib/format'
import type { LoginFromBlockedIpRow } from './loginFromBlockedIpTypes'
import { useLoginFromBlockedIps } from './useLoginFromBlockedIps'

/**
 * Login From Blocked IP — Winner All Logs (Master only).
 *
 * Filters/columns match winner-golive login-from-blocked-ip.html.
 * Live list via Agent-API `loginFromBlockedIp/getLoginFromBlockedIps`.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

export function LoginFromBlockedIpPage() {
  const [page, setPage] = useState(1)
  const [ip, setIp] = useState('')
  const [player, setPlayer] = useState('')
  const [applied, setApplied] = useState({ ip: '', player: '' })

  const { data, isLoading } = useLoginFromBlockedIps({
    page,
    pageSize: PAGE_SIZE,
    ip: applied.ip,
    player: applied.player,
  })

  const columns = useMemo<Column<LoginFromBlockedIpRow>[]>(
    () => [
      {
        id: 'index',
        header: '#',
        align: 'right',
        numeric: true,
        cell: (_row, index) => (page - 1) * PAGE_SIZE + index + 1,
      },
      {
        id: 'ip',
        header: 'IP',
        cell: (row) => row.ip || EMPTY,
      },
      {
        id: 'player',
        header: 'Player',
        cell: (row) => row.player || EMPTY,
      },
      {
        id: 'createdAt',
        header: 'Date',
        cell: (row) => (row.createdAt ? lll(row.createdAt) : EMPTY),
      },
    ],
    [page],
  )

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1)

  const search = () => {
    setPage(1)
    setApplied({ ip: ip.trim(), player: player.trim() })
  }

  return (
    <>
      <PageHead
        title="Login From Blocked IP"
        subtitle="Login attempts from blocked IP addresses — Master All Logs from Winner"
      />

      <Card
        title="Filters"
        subtitle="Search by IP address or player name"
        actions={
          <Button variant="primary" onClick={search}>
            Search
          </Button>
        }
      >
        <div className="w7-row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px' }}>
            <SearchInput
              label="IP"
              value={ip}
              onChange={setIp}
              placeholder="Enter IP address…"
            />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <SearchInput
              label="Player"
              value={player}
              onChange={setPlayer}
              placeholder="Enter player name…"
            />
          </div>
        </div>
      </Card>

      <Card
        flush
        title="Login attempts"
        subtitle={isLoading ? 'Loading' : `${count(total)} in this list`}
        footer={
          <Pager
            page={data?.page ?? page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={PAGE_SIZE}
          />
        }
      >
        <DataTable
          label="Login from blocked IP"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={6}
          empty={
            <StateBlock icon="sliders" title="No data found">
              No login attempts from blocked IPs match these filters.
            </StateBlock>
          }
        />
      </Card>
    </>
  )
}
